// UpdateMarker – "Last updated" date + recent product/rates updates (blogs are NOT included)
// lastUpdated from financial-rates.json; set on any product or rates update. Rates are updated every Monday.

import React, { useState, useEffect } from 'react';

/** Returns ISO date string for the most recent Monday (today if Monday). Used when JSON has no lastUpdated. */
function getLastMondayISO() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

const RATES_JSON_PATH = '/data/financial-rates.json';

/** Product/rates updates only – do NOT add blog posts to this list. */
const RECENT_UPDATES = [
  {
    label: 'Accumulation calculator in the Adviser suite (incl. Monte Carlo)',
    detail: 'Updated 31 Oct 2026. Advisers can run accumulation projections in the Adviser suite, including Monte Carlo stress testing, reconciled with the same retirement engines as Advanced.',
  },
  {
    label: 'Super fees charged per member account',
    detail: 'Updated 29 Oct 2026. Couples now pay the flat admin fee, capped admin % and transaction costs on each partner’s account (as funds charge them), in Basic, Advanced and Adviser. Strategies also wait for your nominated retirement age before drawing super.',
  },
  {
    label: 'Working part-time on Age Pension: $7,800 tax-free (Work Bonus)',
    detail: 'Updated 24 Jan 2026. First $7,800/year of part-time employment income not counted in pension income test. Your + Partner fields in Advanced calculator; each gets $7,800 Work Bonus.',
  },
  {
    label: 'Work Test tool (ages 67–74)',
    detail: 'Check contribution eligibility and work test requirements in the SMSF Suite.',
  },
  {
    label: 'Rates (Centrelink, ATO, etc.) updated every Monday',
    detail: 'Rates from Centrelink, ATO and other sources are updated every Monday.',
  },
  {
    label: '20 September 2026 Age Pension indexation',
    detail: 'Maximum rates now $1,237.70/fn single and $1,866.00/fn couple combined; asset cut-offs and income cut-offs updated; deeming 1.75%/3.75% (thresholds still $66,800 / $110,600 from 1 July 2026).',
  },
];

function formatLastUpdated(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

async function fetchLastUpdated() {
  try {
    // Cache-bust so we always get the live lastUpdated (scraper or build update)
    const url = `${RATES_JSON_PATH}?v=${Date.now()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return getLastMondayISO();
    const data = await res.json();
    const fromJson = data.lastUpdated && String(data.lastUpdated).slice(0, 10);
    return fromJson || getLastMondayISO();
  } catch {
    return getLastMondayISO();
  }
}

/**
 * @param {'banner'|'compact'} [variant='banner']
 */
export default function UpdateMarker({ variant = 'banner' }) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchLastUpdated().then((v) => {
      if (!cancelled) setLastUpdated(v);
    });
    return () => { cancelled = true; };
  }, []);

  const formatted = formatLastUpdated(lastUpdated || getLastMondayISO());

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50/90 border border-emerald-200 rounded-md px-3 py-1.5">
        <span className="font-semibold">Last updated:</span>
        <span>{formatted || '—'}</span>
        <span className="text-emerald-700">(rates updated every Monday)</span>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-y border-emerald-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
              <div>
                <p className="font-bold text-gray-900">Rates (Centrelink, ATO, etc.)</p>
                <p className="text-sm text-gray-600">Last updated: {formatted || '—'}</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 max-w-xl">
              Rates are updated every Monday. The date above changes whenever we update rates or ship a product update (blogs are not part of this register).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium text-sm"
            >
              {expanded ? 'Hide recent updates' : "What's new"}
              <svg
                className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        {expanded && (
          <div className="mt-4 pt-4 border-t border-emerald-200">
            <h4 className="font-semibold text-gray-900 mb-3">Recent updates</h4>
            <ul className="space-y-2">
              {RECENT_UPDATES.map((u, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  <div>
                    <span className="font-medium text-gray-800">{u.label}</span>
                    {u.detail && <span className="text-gray-600"> — {u.detail}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
